import { createContext, useContext, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { get } from '../utils/helpers/api';

const STORAGE_KEY = 'app_settings';

const toDatePickerFormat = (dbFormat) => {
  if (!dbFormat) return 'dd/MM/yyyy';
  return dbFormat.replace(/YYYY/g, 'yyyy').replace(/DD/g, 'dd');
};

const SettingsContext = createContext();

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch {
      return {};
    }
  });

  const updateSettings = (newSettings) => {
    if (!newSettings || typeof newSettings !== 'object') return;
    setSettings(prev => {
      const merged = { ...prev };
      Object.keys(newSettings).forEach(key => {
        merged[key] = { ...(prev[key] || {}), ...newSettings[key] };
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
      return merged;
    });
  };

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    Promise.resolve(get('/settings/preferences'))
      .then(data => {
        if (data.success) {
          if (data.user_settings) updateSettings(data.user_settings);
          if (data.company_settings) updateSettings(data.company_settings);
        }
      })
      .catch(() => {});
  }, []);

  const dbDateFormat = settings?.app?.date_format || 'DD/MM/YYYY';
  const datePickerFormat = toDatePickerFormat(dbDateFormat);

  const timeToken = settings?.app?.time_format === '12h' ? 'h:mm a' : 'HH:mm';
  // react-datepicker uses 'aa' for AM/PM, date-fns uses 'a'
  const timePickerFormat = settings?.app?.time_format === '12h' ? 'h:mm aa' : 'HH:mm';

  const formatDate = (date) => {
    if (!date) return '';
    try {
      const d = typeof date === 'string' ? new Date(date) : date;
      return format(d, datePickerFormat);
    } catch {
      return '';
    }
  };

  const formatTime = (date) => {
    if (!date) return '';
    try {
      let d;
      if (typeof date === 'string' && /^\d{2}:\d{2}/.test(date)) {
        // Time-only string like "09:00:00" — prepend a dummy date
        d = new Date(`1970-01-01T${date}`);
      } else {
        d = typeof date === 'string' ? new Date(date) : date;
      }
      return format(d, timeToken);
    } catch {
      return '';
    }
  };

  const formatDateTime = (date) => {
    if (!date) return '';
    try {
      const d = typeof date === 'string' ? new Date(date) : date;
      return format(d, `${datePickerFormat} ${timeToken}`);
    } catch {
      return '';
    }
  };

  return (
    <SettingsContext.Provider value={{ settings, updateSettings, datePickerFormat, timePickerFormat, formatDate, formatTime, formatDateTime }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
